import { MasterDataAction, set as setMasterData } from '@spike/masterdata-action';
import { NotificationsAction, showError, showSuccess } from '@spike/notifications-action';
import { loginSuccess } from 'actions/login/LoginActions';
import { LoginAction } from 'actions/login/LoginActionsTypes';
import { convert } from 'actions/login/LoginConverter';
import { LoginResponseDto } from 'actions/login/LoginDtos';
import { setMarketplaceDto, MarketplaceAction } from '@spike/marketplace-action';
import ApiClient, { isCancelled } from 'api/ApiClient';
import { removeFromStorage } from 'auth';
import { AxiosResponse } from 'axios';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { ActivateAccountState } from 'reducers/activateAccount/ActivateAccountState';
import { ThunkAction } from 'redux-thunk';
import {
    ActivateAccountAction,
    ACTIVATE_ACCOUNT_END_ACTION_TYPE,
    ACTIVATE_ACCOUNT_FAIL_ACTION_TYPE,
    ACTIVATE_ACCOUNT_RESET_ACTION_TYPE,
    ACTIVATE_ACCOUNT_START_ACTION_TYPE
} from './ActivateAccountActionsTypes';
import { activateAccountHubspot } from 'actions/hubspot/HubSpotActions';
import Analytics from 'analytics/client';
import { Events } from 'analytics/events';

const activateAccountUrl = 'activate_account';

export const activateAccountThunk = (
    token: string
): ThunkAction<
    void,
    ActivateAccountState,
    null,
    | ActivateAccountAction
    | CallHistoryMethodAction
    | NotificationsAction
    | LoginAction
    | MasterDataAction
    | MarketplaceAction
> => {
    return async dispatch => {
        removeFromStorage();

        dispatch(start());

        const HTTP_STATUS_401_UNAUTHORIZED = 401;

        try {
            const response: AxiosResponse<LoginResponseDto> = await ApiClient.post(activateAccountUrl, {
                access_token: token
            });

            // Populate redux state
            dispatch(setMasterData(response.data.master_data));
            dispatch(setMarketplaceDto(response.data.marketplace, response.data.master_data));
            dispatch(loginSuccess(convert(response.data, true)));
            dispatch(activateAccountHubspot(response.data.user.email));
            // Track verification BEFORE redirect so component unmount doesn't swallow the effect.
            try {
                const userId = String(response.data.user.id);
                Analytics.setUserId(userId);
                Analytics.track(Events.EMAIL_VERIFIED, {
                    verification_method: 'email',
                    marketplace_id: response.data.marketplace?.id,
                    user_id: response.data.user.id,
                    staff_id: response.data.user.staff_id
                });
            } catch (e) {
                // Non‑blocking: log but do not interrupt activation flow
                console.warn('Failed to track email_verified', e);
            }
            dispatch(showSuccess('Email verified!'));
        } catch (apiError: any) {
            if (!isCancelled(apiError)) {
                if (apiError.response && apiError.response.status === HTTP_STATUS_401_UNAUTHORIZED) {
                    dispatch(fail());
                    dispatch(showError('Email verification failed!'));
                } else {
                    console.error(apiError);
                    dispatch(showError('Error verifying email!'));
                }
            }
        }
        dispatch(push('/'));
        dispatch(end());
    };
};

export const reset = (): ActivateAccountAction => {
    return {
        type: ACTIVATE_ACCOUNT_RESET_ACTION_TYPE
    };
};

const start = (): ActivateAccountAction => {
    return {
        type: ACTIVATE_ACCOUNT_START_ACTION_TYPE
    };
};

const fail = (): ActivateAccountAction => {
    return {
        type: ACTIVATE_ACCOUNT_FAIL_ACTION_TYPE
    };
};

const end = (): ActivateAccountAction => {
    return {
        type: ACTIVATE_ACCOUNT_END_ACTION_TYPE
    };
};
